import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const CONTEXT_LABEL = {
  sales_agent_upgrade: 'Sales Agent → Buyer Upgrade',
  do_finance_partnership_doc: 'DO Finance — Partnership GST Document',
};

const AdminReviewQueueTab = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [searchText, setSearchText] = useState('');

  const showNotif = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
  };

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('admin_review_requests')
        ?.select('*, user_profiles!admin_review_requests_requested_by_user_id_fkey(full_name, email, mobile_phone)')
        ?.eq('status', 'pending')
        ?.order('created_at', { ascending: true });

      if (error) throw error;
      // Urgent-first, then oldest-first within each tier
      const sorted = [...(data || [])].sort((a, b) => {
        if (a.urgency === b.urgency) return new Date(a.created_at) - new Date(b.created_at);
        return a.urgency === 'urgent' ? -1 : 1;
      });
      setRequests(sorted);
    } catch (err) {
      console.error('AdminReviewQueueTab error:', err?.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    const channel = supabase?.channel('admin_review_requests_tab')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'admin_review_requests' }, () => loadRequests())
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [loadRequests]);

  const filteredRequests = requests.filter(req => {
    const text = searchText.toLowerCase();
    if (!text) return true;
    return [
      req?.user_profiles?.full_name,
      req?.user_profiles?.email,
      req?.user_profiles?.mobile_phone,
      CONTEXT_LABEL?.[req?.context] || req?.context,
    ].some(f => (f || '').toLowerCase().includes(text));
  });

  const handleViewDocument = async (path) => {
    try {
      const { data, error } = await supabase.storage
        .from('review-documents')
        .createSignedUrl(path, 300); // 5-minute link, regenerated on each click
      if (error || !data?.signedUrl) {
        showNotif('Could not open document. Please retry.', 'error');
        return;
      }
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showNotif('Could not open document. Please retry.', 'error');
    }
  };

  const handleApprove = async (req) => {
    setProcessing(p => ({ ...p, [req?.id]: 'approving' }));
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const reviewedBy = session?.user?.email || user?.email;

      const { error } = await supabase.functions.invoke('admin-review-process', {
        body: { request_id: req?.id, action: 'approve', reviewed_by: reviewedBy },
      });

      if (error) throw error;
      showNotif(`Approved. ${req?.user_profiles?.full_name || 'Applicant'} will be notified by email.`);
      loadRequests();
    } catch (err) {
      showNotif(`Failed: ${err?.message}`, 'error');
    } finally {
      setProcessing(p => { const n = { ...p }; delete n[req?.id]; return n; });
    }
  };

  const handleReject = async (req) => {
    setProcessing(p => ({ ...p, [req?.id]: 'rejecting' }));
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const reviewedBy = session?.user?.email || user?.email;

      const { error } = await supabase.functions.invoke('admin-review-process', {
        body: { request_id: req?.id, action: 'reject', reviewed_by: reviewedBy, decision_notes: rejectNotes },
      });

      if (error) throw error;
      showNotif(`Rejected. ${req?.user_profiles?.full_name || 'Applicant'} can resubmit.`);
      setRejectTarget(null);
      setRejectNotes('');
      loadRequests();
    } catch (err) {
      showNotif(`Failed: ${err?.message}`, 'error');
    } finally {
      setProcessing(p => { const n = { ...p }; delete n[req?.id]; return n; });
    }
  };

  return (
    <div className="space-y-5">
      {/* Notification */}
      {notification?.show && (
        <div
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-lg shadow-xl text-sm font-medium text-white"
          style={{
            background: notification?.type === 'error' ? '#dc2626' : '#1e4a72',
            border: `1px solid ${notification?.type === 'error' ? '#b91c1c' : '#2d6aa0'}`,
          }}
        >
          <Icon name={notification?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={18} color="#fff" />
          {notification?.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Review Queue</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading
              ? 'Loading...'
              : `${filteredRequests?.length} pending request${filteredRequests?.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button
          onClick={loadRequests}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
        >
          <Icon name="RefreshCw" size={13} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
        placeholder="Search by name, email, mobile, or request type..."
        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
      />

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 rounded-xl bg-amber-50 border border-amber-100">
          <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400">Loading requests...</p>
        </div>
      )}

      {/* Empty */}
      {!loading && filteredRequests?.length === 0 && (
        <div className="text-center py-16 rounded-xl bg-gray-50 border border-gray-100">
          <Icon name="CheckCircle" size={40} color="#86efac" />
          <p className="mt-3 text-sm font-medium text-gray-400">
            {requests?.length === 0 ? 'No pending review requests' : 'No requests match your search'}
          </p>
          <p className="text-xs mt-1 text-gray-300">
            {requests?.length === 0 ? 'All clear — nothing awaiting approval' : 'Try adjusting your search'}
          </p>
        </div>
      )}

      {/* Request Cards */}
      {!loading && filteredRequests?.length > 0 && (
        <div className="space-y-4">
          {filteredRequests?.map(req => {
            const isProcessing = !!processing?.[req?.id];
            const isRejectOpen = rejectTarget === req?.id;
            const applicant = req?.user_profiles;
            const isUrgent = req?.urgency === 'urgent';

            return (
              <div
                key={req?.id}
                className="bg-white rounded-2xl border shadow-sm p-5"
                style={{ borderColor: isUrgent ? '#fca5a5' : '#e5e7eb' }}
              >
                {/* Top row */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-sm text-gray-900">
                        {applicant?.full_name || 'Unknown Applicant'}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {CONTEXT_LABEL?.[req?.context] || req?.context}
                      </span>
                      {req?.resubmission_of && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          Resubmission
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {applicant?.email} {applicant?.mobile_phone ? `· ${applicant.mobile_phone}` : ''}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Requested {new Date(req?.created_at)?.toLocaleDateString('en-IN', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </p>
                  </div>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap self-start"
                    style={{
                      backgroundColor: isUrgent ? '#fee2e2' : '#fed7aa',
                      color: isUrgent ? '#dc2626' : '#c2410c',
                    }}
                  >
                    {isUrgent ? '⚠️ Urgent — 1 day TAT' : '🕐 Pending Approval'}
                  </span>
                </div>

                {/* Payload / context data */}
                {req?.payload && Object.keys(req.payload).length > 0 && (
                  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100 mb-3 space-y-1">
                    {Object.entries(req.payload).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-xs">
                        <span className="text-gray-400 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="text-gray-700 font-medium">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Documents */}
                {req?.document_paths?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {req.document_paths.map((path, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleViewDocument(path)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700"
                      >
                        <Icon name="FileText" size={13} />
                        Document {i + 1}
                      </button>
                    ))}
                  </div>
                )}

                {/* Reject notes input */}
                {isRejectOpen && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-gray-600 block mb-1">
                      Rejection Reason (shown to applicant)
                    </label>
                    <textarea
                      value={rejectNotes}
                      onChange={e => setRejectNotes(e.target.value)}
                      rows={2}
                      placeholder="e.g. Name on document doesn't match PAN records..."
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3">
                  {!isRejectOpen ? (
                    <>
                      <button
                        onClick={() => handleApprove(req)}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {processing?.[req?.id] === 'approving' ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <Icon name="CheckCircle" size={15} />
                            Approve
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => { setRejectTarget(req?.id); setRejectNotes(''); }}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 rounded-xl border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors disabled:opacity-60"
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleReject(req)}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {processing?.[req?.id] === 'rejecting' ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Rejecting...
                          </>
                        ) : (
                          'Confirm Reject'
                        )}
                      </button>
                      <button
                        onClick={() => { setRejectTarget(null); setRejectNotes(''); }}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReviewQueueTab;
